/**
 * SiteMapPage: world map of every indexed site with clustering, health and activity, and a
 * side panel for text-based triage. The map itself is lazy-loaded behind the canvas port.
 */
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '../../analytics';
import { getAppConfig } from '../../config/appConfig';
import { useAdvancedMode } from '../../hooks/useAdvancedMode';
import { useSiteMap } from '../hooks/useSiteMap';
import { useSiteMapViewState } from '../hooks/useSiteMapViewState';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useNow } from '../hooks/useNow';
import { resolveMapStyleUrl } from '../utils/mapStyle';
import { markerActivation, type ActivationVia } from '../utils/markerActivation';
import { LazySiteMapCanvas } from './map/loadSiteMapCanvas';
import { MapUnavailable } from './map/MapUnavailable';
import type { FocusRequest } from './map/siteMapCanvasPort';
import type { MapViewport, SiteMapSelection } from '../models/siteMapModels';
import { SiteMapSidePanel } from './SiteMapSidePanel';
import './SiteMapPage.css';

/** Used until health has told us its window */
const DEFAULT_RECENT_SHOTS_WINDOW_S = 300;
const CLOCK_TICK_MS = 15_000;

function PageHeader(): JSX.Element {
  return (
    <header className="site-map__header">
      <h1 className="site-map__title">Site Map</h1>
      <p className="site-map__subtitle">Where Toptracer Go sites are active, and how healthy they look.</p>
    </header>
  );
}

export function SiteMapPage(): JSX.Element {
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const { getSearchString } = useAdvancedMode();
  const searchString = getSearchString();
  const { sites, health, entries, retrySites } = useSiteMap();
  const canHover = useMediaQuery('(hover: hover)');
  const now = useNow(CLOCK_TICK_MS);
  // Restored when the user comes back to this visit, e.g. with the browser's back button
  const viewState = useSiteMapViewState();
  const { update: updateViewState } = viewState;

  const [selection, setSelection] = useState<SiteMapSelection>(viewState.initial.selection);
  const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);
  const styleUrl = useMemo(() => resolveMapStyleUrl(getAppConfig()), []);
  const recentShotsWindowS = health.data?.recentShotsWindowS ?? DEFAULT_RECENT_SHOTS_WINDOW_S;

  useEffect(() => {
    track('Pocket Admin Site Map Viewed');
  }, [track]);

  useEffect(() => {
    updateViewState({ selection });
  }, [selection, updateViewState]);

  const onViewportChange = useCallback(
    (viewport: MapViewport) => updateViewState({ viewport }),
    [updateViewState]
  );
  const onSearchQueryChange = useCallback(
    (searchQuery: string) => updateViewState({ searchQuery }),
    [updateViewState]
  );

  const trackOpened = useCallback(
    (siteId: string, source: 'map' | 'panel') => {
      track('Pocket Admin Site Map Site Opened', { SiteId: siteId, Source: source });
    },
    [track]
  );

  const onSiteActivate = useCallback(
    (siteId: string, via: ActivationVia) => {
      if (markerActivation(canHover, via) === 'navigate') {
        trackOpened(siteId, 'map');
        navigate(`/site/${encodeURIComponent(siteId)}${searchString}`);
      } else {
        setSelection({ kind: 'site', siteId });
      }
    },
    [canHover, navigate, searchString, trackOpened]
  );

  const onUnsplittableCluster = useCallback((siteIds: readonly string[]) => {
    setSelection({ kind: 'cluster', siteIds });
  }, []);

  const onShowOnMap = useCallback((siteId: string) => {
    setSelection({ kind: 'site', siteId });
    setFocusRequest((previous) => ({ siteId, seq: (previous?.seq ?? 0) + 1 }));
  }, []);

  const onPanelOpen = useCallback((siteId: string) => trackOpened(siteId, 'panel'), [trackOpened]);
  const onClearSelection = useCallback(() => setSelection(null), []);
  const onLoadError = useCallback((message: string) => setMapError(message), []);

  if (sites.isForbidden) {
    return (
      <div className="site-map site-map--message">
        <PageHeader />
        <p className="site-map__message" role="alert">
          You don't have access to the site map.
        </p>
      </div>
    );
  }

  if (!sites.data) {
    return (
      <div className="site-map site-map--message">
        <PageHeader />
        {sites.isLoading ? (
          <p className="site-map__message" role="status">
            <span className="site-map__spinner" aria-hidden="true" />
            Loading sites…
          </p>
        ) : (
          <div className="site-map__message" role="alert">
            <p>The site list could not be loaded.</p>
            <button type="button" className="site-map-button" onClick={retrySites}>
              Try again
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="site-map">
      <PageHeader />
      <div className="site-map__layout">
        <div className="site-map__map">
          {mapError ? (
            <MapUnavailable message={mapError} />
          ) : (
            <Suspense
              fallback={
                <p className="site-map__map-loading" role="status">
                  Loading map…
                </p>
              }
            >
              <LazySiteMapCanvas
                styleUrl={styleUrl}
                entries={entries}
                recentShotsWindowS={recentShotsWindowS}
                now={now}
                canHover={canHover}
                focusRequest={focusRequest}
                initialViewport={viewState.initial.viewport}
                onViewportChange={onViewportChange}
                onSiteActivate={onSiteActivate}
                onUnsplittableCluster={onUnsplittableCluster}
                onLoadError={onLoadError}
              />
            </Suspense>
          )}
          {entries.length === 0 && (
            <p className="site-map__empty" role="status">
              No sites have a published location yet.
            </p>
          )}
        </div>

        <SiteMapSidePanel
          entries={entries}
          health={health}
          recentShotsWindowS={recentShotsWindowS}
          now={now}
          selection={selection}
          searchString={searchString}
          initialSearchQuery={viewState.initial.searchQuery}
          onSearchQueryChange={onSearchQueryChange}
          onShowOnMap={onShowOnMap}
          onOpen={onPanelOpen}
          onClearSelection={onClearSelection}
        />
      </div>
    </div>
  );
}
